import React, { useState, useEffect } from 'react';
import { Container } from "./styles";
import { FiDownload, FiPlay, FiSearch, FiPlus, FiTrash2, FiCalendar } from "react-icons/fi";
import { GoFile, GoCheck, GoAlert, GoX, GoSync } from "react-icons/go";
import { BiSpreadsheet } from "react-icons/bi";
import DownloadKeysModal from "./components/DownloadKeysModal";
import api from '../../services/api';
import { toast } from 'react-toastify';

export default function XmlDownload({ embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [downloads, setDownloads] = useState([]);
  const [downloadSearch, setDownloadSearch] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [progressLogs, setProgressLogs] = useState([]);
  const [currentProgress, setCurrentProgress] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [downloadingZip, setDownloadingZip] = useState(null);
  const [downloadProgress, setDownloadProgress] = useState({});

  // Carregar lista de downloads ao montar componente
  useEffect(() => {
    fetchDownloads();
  }, []);

  const fetchDownloads = async () => {
    setLoading(true);
    try {
      const response = await api.get('/xml-download/filter-outputs');
      
      if (response.data.outputs) {
        // Transformar dados da API para formato do componente
        const formattedDownloads = response.data.outputs.map(output => {
          const stats = output.estatisticas || {};
          const filters = output.filtrosAplicados || {};
          
          // Gerar período baseado nos filtros ou data
          let periodo = 'Download';
          if (filters.dataInicio && filters.dataFim) {
            const inicio = new Date(filters.dataInicio);
            const fim = new Date(filters.dataFim);
            periodo = `${inicio.toLocaleDateString('pt-BR')} - ${fim.toLocaleDateString('pt-BR')}`;
          } else if (filters.dataInicio) {
            periodo = `A partir de ${new Date(filters.dataInicio).toLocaleDateString('pt-BR')}`;
          } else if (filters.dataFim) {
            periodo = `Até ${new Date(filters.dataFim).toLocaleDateString('pt-BR')}`;
          }
          
          return {
            id: output.outputId,
            periodo,
            date: output.geradoEm,
            totalFiltradas: stats.totalFiltradas || 0,
            encontradasEmDisco: stats.encontradasEmDisco || 0,
            faltantesEmDisco: stats.faltantesEmDisco || 0,
            tamanhoZipMB: stats.tamanhoZipMB || 0,
            hasZip: output.hasZip,
            hasFaltantes: output.hasFaltantes,
            filtros: filters
          };
        });
        
        // Ordenar por data mais recente primeiro
        formattedDownloads.sort((a, b) => new Date(b.date) - new Date(a.date));
        
        setDownloads(formattedDownloads);
      }
    } catch (error) {
      console.error('Erro ao carregar downloads:', error);
      toast.error('Erro ao carregar histórico de downloads');
    } finally {
      setLoading(false);
    }
  };

  // Calcular totais gerais
  const totalStats = downloads.reduce((acc, download) => ({
    totalFiltradas: acc.totalFiltradas + (download.totalFiltradas || 0),
    encontradasEmDisco: acc.encontradasEmDisco + (download.encontradasEmDisco || 0),
    faltantesEmDisco: acc.faltantesEmDisco + (download.faltantesEmDisco || 0),
    tamanhoZipMB: acc.tamanhoZipMB + (download.tamanhoZipMB || 0)
  }), { totalFiltradas: 0, encontradasEmDisco: 0, faltantesEmDisco: 0, tamanhoZipMB: 0 });

  const lastDownload = downloads.length > 0 
    ? new Date(downloads[0].date).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-';

  const handleStartDownload = () => {
    setShowDownloadModal(true);
  };

  const handleDeleteDownload = (outputId, downloadName) => {
    setDeleteConfirm({ outputId, downloadName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    
    const { outputId } = deleteConfirm;
    
    try {
      // Remover imediatamente da lista (otimistic update)
      setDownloads(prev => prev.filter(d => d.id !== outputId));
      setDeleteConfirm(null);
      
      // Deletar no backend
      await api.delete(`/xml-download/filter-output/${outputId}`);
      toast.success('Download deletado com sucesso');
    } catch (error) {
      console.error('Erro ao deletar download:', error);
      toast.error('Erro ao deletar download');
      // Recarregar lista em caso de erro
      fetchDownloads();
    }
  };

  const handleDownloadZip = async (outputId) => {
    try {
      setDownloadingZip(outputId);
      setDownloadProgress(prev => ({ ...prev, [outputId]: 0 }));
      
      const response = await api.get(`/xml-download/download-filter-zip?outputId=${outputId}`, {
        responseType: 'blob',
        timeout: 600000,
        onDownloadProgress: (progressEvent) => {
          const percentCompleted = progressEvent.total ? Math.round((progressEvent.loaded * 100) / progressEvent.total) : 0;
          setDownloadProgress(prev => ({ ...prev, [outputId]: percentCompleted }));
        }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `xmls_${outputId}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download concluído!');
      
      setTimeout(() => {
        setDownloadingZip(null);
        setDownloadProgress(prev => {
          const newProgress = { ...prev };
          delete newProgress[outputId];
          return newProgress;
        });
      }, 1000);
    } catch (error) {
      console.error('Erro ao baixar ZIP:', error);
      toast.error('Erro ao baixar arquivo ZIP');
      setDownloadingZip(null);
      setDownloadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[outputId];
        return newProgress;
      });
    }
  };

  const handleDownload = async (keys) => {
    console.log('Processar download de XMLs:', keys);
    setDownloading(true);
    setProgressLogs([]);
    setCurrentProgress(null);
    
    try {
      // TODO: Implementar chamada à API
      // const response = await api.post('/xml-download/by-keys', { keys });
      
      // Simular processamento
      console.log('Download iniciado para', keys.length, 'chaves');
      
    } catch (error) {
      console.error('Erro ao processar download:', error);
      alert('Erro ao processar download: ' + error.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Container>
      {!embedded && (
        <>
          {/* Header */}
          <div className="header-info">
            <div className="header-icon">
              <FiDownload />
            </div>
            <div className="header-details">
              <h3>Download de XMLs</h3>
              <div className="header-meta">
                <span className="status-tag active">Sistema Ativo</span>
                <span className="update-tag">
                  {downloads.length > 0 ? `Último download: ${lastDownload}` : 'Aguardando primeiro download'}
                </span>
              </div>
            </div>
          </div>

          {/* Tabs - Iniciar Download */}
          <div className="tabs-header">
            <button 
              className="tab active"
              onClick={handleStartDownload}
              disabled={downloading}
            >
              {downloading ? <GoSync className="spin" /> : <FiPlay />}
              {downloading ? 'Baixando XMLs...' : 'Iniciar Download'}
            </button>
          </div>
        </>
      )}

      {/* KPI Cards */}
      {loading ? (
        <div className="kpi-cards main-stats">
          <div className="kpi-card primary">
            <div className="card-content">
              <div className="thumb"><GoSync className="spin" /></div>
              <div className="text">
                <span className="kpi-label">Carregando...</span>
                <span className="kpi-value">-</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="kpi-cards main-stats">
          <div className="kpi-card primary">
            <div className="card-content">
              <div className="thumb"><GoFile /></div>
              <div className="text">
                <span className="kpi-label">Total Filtradas</span>
                <span className="kpi-value">{totalStats.totalFiltradas.toLocaleString()}</span>
              </div>
            </div>
          </div>

          <div className="kpi-card success with-progress">
            <div className="card-content">
              <div className="thumb"><GoCheck /></div>
              <div className="text">
                <span className="kpi-label">Encontradas</span>
                <span className="kpi-value">{totalStats.encontradasEmDisco.toLocaleString()}</span>
              </div>
            </div>
            <div className="kpi-progress">
              <div className="kpi-progress-fill" style={{width: `${totalStats.totalFiltradas > 0 ? (totalStats.encontradasEmDisco / totalStats.totalFiltradas) * 100 : 0}%`}} />
            </div>
          </div>

          <div className="kpi-card rejected with-progress">
            <div className="card-content">
              <div className="thumb"><GoX /></div>
              <div className="text">
                <span className="kpi-label">Faltantes</span>
                <span className="kpi-value">{totalStats.faltantesEmDisco.toLocaleString()}</span>
              </div>
            </div>
            <div className="kpi-progress">
              <div className="kpi-progress-fill" style={{width: `${totalStats.totalFiltradas > 0 ? (totalStats.faltantesEmDisco / totalStats.totalFiltradas) * 100 : 0}%`}} />
            </div>
          </div>

          <div className="kpi-card warning">
            <div className="card-content">
              <div className="thumb"><FiDownload /></div>
              <div className="text">
                <span className="kpi-label">Tamanho Total</span>
                <span className="kpi-value">{totalStats.tamanhoZipMB.toFixed(1)} MB</span>
              </div>
            </div>
          </div>

        </div>
      )}

      {/* Toolbar - Downloads Realizados e Busca */}
      <div className="toolbar">
        <div className="toolbar-left">
          <button className="toolbar-btn">
            <BiSpreadsheet />
            Downloads Realizados
          </button>
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar download por ID ou período..."
              value={downloadSearch}
              onChange={(e) => setDownloadSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Lista de Downloads */}
      <div className="downloads-section">
        <div className="downloads-grid">
          {/* Card Novo Download */}
          <div className="download-card new-download" onClick={handleStartDownload}>
            <div className="new-download-content">
              <div className="new-download-icon">
                <FiPlus />
              </div>
              <div className="new-download-text">
                <h5>Novo Download</h5>
                <p>Baixar XMLs por filtros personalizados</p>
              </div>
            </div>
          </div>

          {/* Downloads Existentes */}
          {downloads
            .filter(download => 
              download.id.toLowerCase().includes(downloadSearch.toLowerCase()) ||
              (download.periodo && download.periodo.toLowerCase().includes(downloadSearch.toLowerCase()))
            )
            .map(download => {
            const percentEncontradas = download.totalFiltradas > 0 
              ? Math.round((download.encontradasEmDisco / download.totalFiltradas) * 100) 
              : 0;
            
            return (
              <div 
                key={download.id} 
                className="download-card"
                onClick={() => download.hasZip && handleDownloadZip(download.id)}
                style={{ cursor: download.hasZip ? 'pointer' : 'default' }}
                title={download.hasZip ? 'Clique para baixar ZIP' : 'ZIP não disponível'}
              >
                <span className="file-size-left">{download.tamanhoZipMB.toFixed(1)} MB</span>
                <button 
                  className="delete-btn-corner"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteDownload(download.id, download.periodo || download.id);
                  }}
                  title="Deletar download"
                >
                  <FiTrash2 />
                </button>
                
                <div className="download-header">
                  <div className={`download-icon ${downloadingZip === download.id ? 'downloading' : ''}`}>
                    {downloadingZip === download.id ? <GoSync className="spin" /> : <FiDownload />}
                  </div>
                  <div className="download-info">
                    <h5>{download.id}</h5>
                    <span className="periodo">{download.periodo}</span>
                    <span className="date">{new Date(download.date).toLocaleString('pt-BR')}</span>
                  </div>
                </div>
                
                {downloadingZip === download.id && downloadProgress[download.id] !== undefined && (
                  <div className="download-progress-bar">
                    <div 
                      className="download-progress-fill" 
                      style={{ width: `${downloadProgress[download.id]}%` }}
                    />
                    <span className="download-progress-text">{downloadProgress[download.id]}%</span>
                  </div>
                )}
                
                <div className="download-meta">
                  <div className="meta-item">
                    <GoFile />
                    <span className="meta-value">{download.totalFiltradas.toLocaleString()}</span>
                  </div>
                  <div className="meta-item success">
                    <GoCheck />
                    <span className="meta-value">{download.encontradasEmDisco.toLocaleString()}</span>
                  </div>
                  <div className="meta-item error">
                    <GoX />
                    <span className="meta-value">{download.faltantesEmDisco.toLocaleString()}</span>
                  </div>
                </div>

                <div className="download-footer">
                  <span><FiCalendar /> {download.periodo}</span>
                  <span className="percent-badge" style={{
                    color: percentEncontradas >= 90 ? '#10b981' : percentEncontradas >= 70 ? '#f59e0b' : '#ef4444'
                  }}>
                    {percentEncontradas}% encontradas
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal de Download por Chaves */}
      <DownloadKeysModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownload}
        onComplete={fetchDownloads}
        progressLogs={progressLogs}
        currentProgress={currentProgress}
        isProcessing={downloading}
      />

      {/* Modal de Confirmação de Deleção */}
      {deleteConfirm && (
        <div className="delete-modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="delete-modal-icon">
              <FiTrash2 />
            </div>
            <div className="delete-modal-content">
              <h4>Excluir download</h4>
              <p>Tem certeza que deseja excluir o download</p>
              <span className="delete-modal-filename">
                {deleteConfirm.downloadName.length > 40
                  ? deleteConfirm.downloadName.substring(0, 40) + '...'
                  : deleteConfirm.downloadName
                }
              </span>
              <p className="delete-modal-warning">Esta ação não pode ser desfeita.</p>
            </div>
            <div className="delete-modal-actions">
              <button className="delete-modal-cancel" onClick={() => setDeleteConfirm(null)}>
                Cancelar
              </button>
              <button className="delete-modal-confirm" onClick={confirmDelete}>
                <FiTrash2 />
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
